import { TaskRow } from "./TaskRow";
import { CardInfo, StatsHeader } from "../StatsHeader";
import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Skeleton } from "@src/components";
import { RewardsState } from "@src/contexts";

export const Tasks = ({
  tasks,
  headerData,
  referralCode,
  claimTask,
  verifyTask,
  isLoading,
}: {
  tasks: RewardsState["tasks"];
  headerData: {
    completedTasks: { curr: number; prev: number };
    points: { curr: number; prev: number };
  };
  referralCode: string;
  claimTask: (taskId: string) => void;
  verifyTask: (taskId: string) => void;
  isLoading: boolean;
}) => {
  const hasMounted = useRef(false);

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const { completedTasks, points } = headerData;

  const statsCards: { leftCard: CardInfo; rightCard: CardInfo } = {
    leftCard: {
      title: "Claimed tasks",
      value: completedTasks.curr,
      prevValue: completedTasks.prev,
    },
    rightCard: {
      title: "Points from tasks",
      value: points.curr,
      prevValue: points.prev,
    },
  };

  return (
    <motion.div className="flex flex-col gap-2">
      <StatsHeader cards={statsCards} isLoading={isLoading} />

      {isLoading ? (
        [...Array(7)].map((_, i) => (
          <motion.div key={`skeleton-${i}`} layout>
            <div className="flex justify-between p-4 gap-2 items-center rounded-xl bg-muted/10">
              <div className="flex gap-2 items-center">
                <Skeleton className="w-6 h-6" />
                <div className="flex flex-col gap-1">
                  {i % 2 === 0 ? (
                    <Skeleton className="h-4 w-36" />
                  ) : (
                    <>
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-40" />
                    </>
                  )}
                </div>
              </div>

              <Skeleton className="w-16 h-6" />
            </div>
          </motion.div>
        ))
      ) : (
        <AnimatePresence mode="sync">
          {tasks.map((t) => (
            <motion.div
              key={`${t.taskId}-${t.claimed}`}
              layout
              initial={hasMounted.current ? { opacity: 0, scale: 0.98 } : false}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, x: -50, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <TaskRow
                task={t}
                onClaim={claimTask}
                referralCode={referralCode}
                verifyTask={verifyTask}
              />
            </motion.div>
          ))}{" "}
        </AnimatePresence>
      )}
    </motion.div>
  );
};
